import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  TrendingUp,
  CalendarIcon,
  Loader2,
  CheckCircle2,
  Receipt,
  Car,
  Banknote,
  ArrowDown,
  CalendarRange,
} from "lucide-react";
import { cn } from "@/lib/utils";

const ProfitExpenseSummaryPage = () => {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const isSameDay = format(fromDate, "yyyy-MM-dd") === format(toDate, "yyyy-MM-dd");

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const from = format(fromDate, "yyyy-MM-dd");
      const to = format(toDate, "yyyy-MM-dd");

      let url;
      if (isSameDay) {
        url = `/profit-expense-summary?date=${from}`;
      } else {
        url = `/profit-expense-summary?from_date=${from}&to_date=${to}`;
      }

      const response = await api.get(url);
      setData(response.data.data);
    } catch (error) {
      console.error("Error fetching profit expense summary:", error);
      toast.error("Failed to fetch summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [fromDate, toDate]);

  const dateLabel = isSameDay
    ? format(fromDate, "dd MMM yyyy")
    : `${format(fromDate, "dd MMM yyyy")} — ${format(toDate, "dd MMM yyyy")}`;

  return (
    <div className="space-y-6" data-testid="profit-expense-summary-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-primary-950">Profit & Expense Summary</h1>
          <p className="text-muted-foreground text-sm">Daily gross profit and detailed expense breakdown</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="from-date-btn">
                <CalendarIcon size={14} className="mr-1.5" />
                From: {format(fromDate, "dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={fromDate} onSelect={(d) => { if (d) { setFromDate(d); if (d > toDate) setToDate(d); } }} initialFocus />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="to-date-btn">
                <CalendarIcon size={14} className="mr-1.5" />
                To: {format(toDate, "dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={toDate} onSelect={(d) => { if (d) { setToDate(d); if (d < fromDate) setFromDate(d); } }} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      ) : !data ? (
        <div className="text-center py-24 text-muted-foreground">No data available for this period</div>
      ) : (
        <div className="space-y-6">
          {/* Submitted badge */}
          {data.is_submitted && isSameDay && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 border border-green-200 rounded-lg px-4 py-2 w-fit">
              <CheckCircle2 size={16} />
              Summary submitted for {dateLabel}
            </div>
          )}

          {/* Gross Profit Card */}
          <Card className="border-purple-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-muted-foreground">
                Gross Profit {!isSameDay && <span className="text-xs font-normal ml-2">({dateLabel})</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-3xl font-bold text-purple-600" data-testid="gross-profit-value">{formatCurrency(data.gross_profit)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Total Sale ({formatCurrency(data.total_sale)}) - COGS ({formatCurrency(data.net_purchase)})
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Expenses Breakdown */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <ArrowDown size={18} className="text-red-500" />
              Expenses Breakdown
            </h2>

            {/* General Expenses */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt size={18} className="text-orange-500" />
                    General Expenses
                  </div>
                  <span className="text-orange-600 font-bold">{formatCurrency(data.general_total)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {data.general_expenses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No expenses</p>
                ) : (
                  <div className="divide-y">
                    {data.general_expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <p className="text-sm font-medium">{exp.category || "General"}</p>
                          {exp.description && <p className="text-xs text-muted-foreground">{exp.description}</p>}
                        </div>
                        <span className="text-sm font-semibold text-red-600">{formatCurrency(exp.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transportation Expenses */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car size={18} className="text-blue-500" />
                    Transportation Expenses
                  </div>
                  <span className="text-blue-600 font-bold">{formatCurrency(data.transportation_total)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {data.transportation_expenses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No transportation expenses</p>
                ) : (
                  <div className="divide-y">
                    {data.transportation_expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <p className="text-sm font-medium">{exp.description}</p>
                          {exp.comments && <p className="text-xs text-muted-foreground">{exp.comments}</p>}
                        </div>
                        <span className="text-sm font-semibold text-red-600">{formatCurrency(exp.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Salary Expenses */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Banknote size={18} className="text-green-500" />
                    Salary Expenses
                  </div>
                  <span className="text-green-600 font-bold">{formatCurrency(data.salary_total)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {data.salary_expenses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No salary expenses</p>
                ) : (
                  <div className="divide-y">
                    {data.salary_expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <p className="text-sm font-medium">{exp.description}</p>
                          {exp.remarks && <p className="text-xs text-muted-foreground">{exp.remarks}</p>}
                        </div>
                        <span className="text-sm font-semibold text-red-600">{formatCurrency(exp.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Salesman Expenses */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Receipt size={18} className="text-purple-500" />
                    Salesman Expenses
                  </div>
                  <span className="text-purple-600 font-bold">{formatCurrency(data.salesman_expense_total || 0)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {!data.salesman_expenses || data.salesman_expenses.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-2">No salesman expenses</p>
                ) : (
                  <div className="divide-y">
                    {data.salesman_expenses.map((exp) => (
                      <div key={exp.id} className="flex items-center justify-between py-2.5">
                        <div>
                          <p className="text-sm font-medium">{exp.description}</p>
                          <div className="flex gap-3 mt-0.5">
                            {exp.food_expense > 0 && <span className="text-[11px] text-muted-foreground">Food: {formatCurrency(exp.food_expense)}</span>}
                            {exp.diesel_expense > 0 && <span className="text-[11px] text-muted-foreground">Diesel: {formatCurrency(exp.diesel_expense)}</span>}
                            {exp.other_expense > 0 && <span className="text-[11px] text-muted-foreground">Other: {formatCurrency(exp.other_expense)}</span>}
                          </div>
                        </div>
                        <span className="text-sm font-semibold text-red-600">{formatCurrency(exp.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {data.salesman_expenses && data.salesman_expenses.length > 0 && (data.salesman_food_total > 0 || data.salesman_diesel_total > 0 || data.salesman_other_total > 0) && (
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">By Category:</span>
                    {data.salesman_food_total > 0 && <span>Food: <span className="font-semibold text-foreground">{formatCurrency(data.salesman_food_total)}</span></span>}
                    {data.salesman_diesel_total > 0 && <span>Diesel: <span className="font-semibold text-foreground">{formatCurrency(data.salesman_diesel_total)}</span></span>}
                    {data.salesman_other_total > 0 && <span>Other: <span className="font-semibold text-foreground">{formatCurrency(data.salesman_other_total)}</span></span>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Total Expenses + Damage Loss + Net Profit */}
          <div className="space-y-3">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="pt-5 pb-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Expenses</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    General ({formatCurrency(data.general_total)}) + Transportation ({formatCurrency(data.transportation_total)}) + Salary ({formatCurrency(data.salary_total)}) + Salesman ({formatCurrency(data.salesman_expense_total || 0)})
                  </p>
                </div>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(data.total_expenses)}</p>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50" data-testid="damage-loss-card">
              <CardContent className="pt-5 pb-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Damage Loss</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Damaged crates valued at avg buy rate
                  </p>
                </div>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(data.damage_loss || 0)}</p>
              </CardContent>
            </Card>

            {(() => {
              const calcNetProfit = data.gross_profit - data.total_expenses - (data.damage_loss || 0);
              return (
                <Card className={cn("border-2", calcNetProfit >= 0 ? "border-green-300 bg-green-50" : "border-red-300 bg-red-50")} data-testid="net-profit-card">
                  <CardContent className="pt-5 pb-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Net Profit</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Gross Profit ({formatCurrency(data.gross_profit)}) - Total Expenses ({formatCurrency(data.total_expenses)}) - Damage Loss ({formatCurrency(data.damage_loss || 0)})
                      </p>
                    </div>
                    <p className={cn("text-3xl font-bold", calcNetProfit >= 0 ? "text-green-600" : "text-red-600")}>
                      {formatCurrency(calcNetProfit)}
                    </p>
                  </CardContent>
                </Card>
              );
            })()}
          </div>

          {/* Day-wise Net Profit Breakdown (only for date ranges) */}
          {!isSameDay && data.daily_breakdown && data.daily_breakdown.length > 0 && (
            <Card className="border-border/50" data-testid="daily-breakdown-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <CalendarRange size={18} className="text-indigo-500" />
                  Day-wise Net Profit
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="text-left py-2.5 px-3 font-medium text-muted-foreground">Date</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Sale</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">COGS</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Gross Profit</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Expenses</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Damage</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Net Profit</th>
                        <th className="text-right py-2.5 px-3 font-medium text-muted-foreground">Day by Day</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        let cumulative = 0;
                        const rows = data.daily_breakdown.map((day) => {
                          const dayNetProfit = day.gross_profit - day.total_expenses - day.damage_loss;
                          cumulative += dayNetProfit;
                          return (
                            <tr key={day.date} className="border-b last:border-b-0 hover:bg-muted/20">
                              <td className="py-2.5 px-3 font-medium">{format(new Date(day.date + "T00:00:00"), "dd MMM yyyy")}</td>
                              <td className="py-2.5 px-3 text-right">{formatCurrency(day.total_sale)}</td>
                              <td className="py-2.5 px-3 text-right">{formatCurrency(day.net_purchase)}</td>
                              <td className="py-2.5 px-3 text-right text-purple-600">{formatCurrency(day.gross_profit)}</td>
                              <td className="py-2.5 px-3 text-right text-red-600">{formatCurrency(day.total_expenses)}</td>
                              <td className="py-2.5 px-3 text-right text-amber-600">{formatCurrency(day.damage_loss)}</td>
                              <td className={cn("py-2.5 px-3 text-right font-semibold", dayNetProfit >= 0 ? "text-green-600" : "text-red-600")}>
                                {formatCurrency(dayNetProfit)}
                              </td>
                              <td className={cn("py-2.5 px-3 text-right font-semibold", cumulative >= 0 ? "text-blue-600" : "text-red-600")}>
                                {formatCurrency(cumulative)}
                              </td>
                            </tr>
                          );
                        });
                        const totSale = data.daily_breakdown.reduce((s, d) => s + d.total_sale, 0);
                        const totCogs = data.daily_breakdown.reduce((s, d) => s + d.net_purchase, 0);
                        const totGross = data.daily_breakdown.reduce((s, d) => s + d.gross_profit, 0);
                        const totExp = data.daily_breakdown.reduce((s, d) => s + d.total_expenses, 0);
                        const totDmg = data.daily_breakdown.reduce((s, d) => s + d.damage_loss, 0);
                        const totNet = totGross - totExp - totDmg;
                        rows.push(
                          <tr key="total" className="bg-muted/60 font-semibold border-t-2">
                            <td className="py-2.5 px-3">Total</td>
                            <td className="py-2.5 px-3 text-right">{formatCurrency(totSale)}</td>
                            <td className="py-2.5 px-3 text-right">{formatCurrency(totCogs)}</td>
                            <td className="py-2.5 px-3 text-right text-purple-600">{formatCurrency(totGross)}</td>
                            <td className="py-2.5 px-3 text-right text-red-600">{formatCurrency(totExp)}</td>
                            <td className="py-2.5 px-3 text-right text-amber-600">{formatCurrency(totDmg)}</td>
                            <td className={cn("py-2.5 px-3 text-right", totNet >= 0 ? "text-green-600" : "text-red-600")}>
                              {formatCurrency(totNet)}
                            </td>
                            <td className={cn("py-2.5 px-3 text-right", cumulative >= 0 ? "text-blue-600" : "text-red-600")}>
                              {formatCurrency(cumulative)}
                            </td>
                          </tr>
                        );
                        return rows;
                      })()}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfitExpenseSummaryPage;
